/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable, Optional} from '@angular/core';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import * as _ from 'lodash';
import {map, Observable, of, switchMap, throwError} from 'rxjs';

import {AdnApiClientService} from '../common/controller/adn-api-client.service';
import {
  SignalAdminResourceApiBase,
  SignalAdminResourceListParams,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from '../common/controller/signal-admin-resource-api';
import {OrderApiService} from '../order/order-api.service';
import {transformLineItemRequest, transformLineItemResponse} from './line-item-api-transforms';

export {transformLineItemRequest, transformLineItemResponse} from './line-item-api-transforms';

export interface LineItemModel extends SignalAdminResourceModel {
  [key: string]: any;
}

export interface LineItemEditApi {
  save(model: LineItemModel): Observable<LineItemModel>;
  savePartial(
    model: LineItemModel,
    fields?: string | string[],
    additionalParams?: Record<string, any>,
    endpoint?: string
  ): Observable<LineItemModel>;
  dimensionsCheck(id: string, limitToRequestedInLastDay?: boolean): Observable<any>;
  getPublisherApprovals(id: string): Observable<any>;
}

@Injectable({providedIn: 'root'})
export class LineItemApiService extends SignalAdminResourceApiBase<LineItemModel> {
  constructor(client: AdnApiClientService, private readonly orderApi: OrderApiService, @Optional() localUserProfile?: LocalUserProfile) {
    super(client, 'lineitems', {showBudgetSpend: true}, () => localUserProfile?.isReadOnly() || false);
  }

  override get(id: string): Observable<LineItemModel> {
    return super.get(id).pipe(map(transformLineItemResponse));
  }

  override list(params: SignalAdminResourceListParams): Observable<SignalAdminResourcePage<LineItemModel>> {
    return super.list(params).pipe(map((page) => ({
      ...page,
      results: (page.results || []).map(transformLineItemResponse)
    })));
  }

  override save(model: LineItemModel): Observable<LineItemModel> {
    const payload = transformLineItemRequest(model);
    return this.saveToEndpoint(payload, '');
  }

  protected override persistPartial(payload: LineItemModel, endpoint: string): Observable<LineItemModel> {
    return this.saveToEndpoint(transformLineItemRequest(payload), endpoint);
  }

  bulkSave(modelsOrParams: Partial<LineItemModel>[] | Record<string, any>, models?: Partial<LineItemModel>[]): Observable<void> {
    const params = models ? modelsOrParams as Record<string, any> : {};
    const payload = models || modelsOrParams as Partial<LineItemModel>[];
    return this.client.request<void>({
      method: 'POST',
      url: this.resourceUri('lineitems'),
      params: {showBudgetSpend: true, ...params},
      body: payload
    });
  }

  listOptions(params: Record<string, any> = {}): Observable<SignalAdminResourcePage<LineItemModel>> {
    return this.client.request<SignalAdminResourcePage<LineItemModel>>({
      method: 'GET',
      url: this.resourceUri('lineitems'),
      params: {showBudgetSpend: true, ...params}
    }).pipe(map((page) => ({...page, results: (page.results || []).map(transformLineItemResponse)})));
  }

  getHydrated(id: string): Observable<LineItemModel> {
    return this.get(id).pipe(switchMap((lineItem) => {
      if (!lineItem.order?.id) {
        return of(lineItem);
      }
      return this.orderApi.getDataView({id: lineItem.order.id}).pipe(
        map((order) => ({...lineItem, order}))
      );
    }));
  }

  dimensionsCheck(id: string, limitToRequestedInLastDay = true): Observable<any> {
    return this.client.request({
      method: 'GET',
      url: this.resourceUri(['lineitems', id, 'dimensionsCheck']),
      params: {showBudgetSpend: true, limitToRequestedInLastDay}
    }).pipe(map((response) => response || {}));
  }

  getPublisherApprovals(id: string): Observable<any> {
    return this.client.request({
      method: 'GET',
      url: this.resourceUri(['lineitems', id, 'publisherapprovals']),
      params: {showBudgetSpend: true}
    });
  }

  copyLineItem(
    lineItem: LineItemModel,
    newId: string,
    copyTargeting = true,
    copyCreatives = true,
    copyReportSchedules = true
  ): Observable<void> {
    if (!lineItem?.id) {
      return throwError(() => ({data: {errors: [{error: {text: 'Insufficient parameters for the line item copy'}}]}}));
    }
    const body = {
      id: lineItem.id,
      orderId: _.get(lineItem, ['order', 'id'], ''),
      lineItemId: newId || '',
      name: lineItem.name,
      copyTargeting,
      copyCreatives,
      copyReportSchedules,
      userState: lineItem.userState || ''
    };
    return this.client.request<void>({
      method: 'POST',
      url: this.resourceUri(['lineitems', lineItem.id, 'copy']),
      params: {showBudgetSpend: true, ..._.omit(body, 'id')},
      body
    });
  }

  private saveToEndpoint(payload: LineItemModel, endpoint: string): Observable<LineItemModel> {
    const path = endpoint ? ['lineitems', payload.id, endpoint] : ['lineitems', payload.id];
    const params = _.pick(payload, [
      'concise',
      'ignoreProduct',
      'updateOnlyTheseTargetTypes',
      'skipTeamBasedTargeting',
      'skipTeamBasedTargetingType'
    ]);
    return this.client.request<LineItemModel>({
      method: 'POST',
      url: this.resourceUri(path),
      params: {showBudgetSpend: true, ...params},
      body: payload
    }).pipe(map((saved) => transformLineItemResponse({...payload, ...(saved || {})})));
  }
}
